import { useCallback, useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Comments, when } from "@/components/ContentBits";
import { Avatar } from "@/components/Avatar";
import { Users, Heart, Plus } from "lucide-react";
import { Modal } from "@/components/FindPeople";
import { useDialog } from "@/components/Dialogs";
import { PostComposer, PostCard, PostImages } from "@/components/Posts";
import { ListCard } from "@/pages/ListsTab";
import { NoteCard } from "@/pages/NotesTab";
import { DiaryPoster, ReactionBar, ratingLabel, EntryExtras } from "@/components/DiaryPoster";

// The feed refreshes every minute. A photo keeps its link between refreshes (links last an hour, so
// we keep one for 40 minutes) so the browser doesn't download every photo again each time.
const KEEP_MS = 40 * 60 * 1000;
export function keepPhotoLinks(prev, next) {
  const now = Date.now();
  const old = new Map();
  (prev || []).forEach((p) => (p.images || []).forEach((im) => old.set(im.id, im)));
  return next.map((it) => (it.images && it.images.length
    ? { ...it, images: it.images.map((im) => { const o = old.get(im.id); return o && now - o.seen < KEEP_MS ? o : { ...im, seen: now }; }) }
    : it));
}

// New posts written in the Feed start as friends-only (change this one word to "public" to make Everyone the starting choice there too).
const FEED_POST_DEFAULT = "friends";

const FILTERS = [["all", "All"], ["entry", "Diary"], ["post", "Posts"], ["list", "Lists"], ["note", "Notes"]];

const DID = { post: "posted", entry: "shared a diary entry", list: "shared a list", note: "shared a note" };

/** Who this is from: their picture, their name big, and what they did and when. Sits above every card in the feed. */
export function FeedAuthor({ item }) {
  const mine = !!item.mine_post || !!item.mine_item;
  return (
    <div className="mb-2 flex items-center gap-3 px-1" data-testid="feed-author">
      <Link to={`/u/${item.username}`} aria-label={`${item.username}'s profile`} className="flex-shrink-0"><Avatar avatar={item.avatar} url={item.avatar_url} name={item.username || "?"} /></Link>
      <div className="min-w-0 flex-1 leading-tight">
        <p className="truncate text-lg font-semibold">
          <Link to={`/u/${item.username}`} className="text-[hsl(var(--gold))] hover:underline" data-testid="feed-author-name">{item.username}</Link>
          {mine && <span className="ml-2 text-sm font-normal text-muted-foreground">(you)</span>}
        </p>
        <p className="truncate text-sm text-muted-foreground" data-testid="feed-author-did">{DID[item.kind] || "shared"} · {when(item.created_at)}</p>
      </div>
    </div>
  );
}

/** A friend's diary entry (a show they've seen), in the feed. Reactions only; comments live on posts, lists and notes. */
export function FeedEntryCard({ entry: e, onChange }) {
  return (
    <article data-testid="feed-entry" className="flex overflow-hidden rounded-2xl border border-border bg-card">
      <div className="w-[4.5rem] flex-shrink-0 self-stretch bg-secondary sm:w-[5.5rem]" style={{ minHeight: "7rem" }}>
        <DiaryPoster src={e.image_url} name={e.show_name} />
      </div>
      <div className="min-w-0 flex-1 p-4">
        <p className="font-serif text-lg font-semibold leading-tight">{e.show_name}</p>
        <p className="text-sm text-muted-foreground">{[e.venue, e.date].filter(Boolean).join(" · ")}</p>
        {e.rating ? <p className="mt-1 text-sm text-[hsl(var(--gold))]">{"★".repeat(e.rating)}<span className="text-muted-foreground/40">{"★".repeat(5 - e.rating)}</span> <span className="ml-1 text-xs">{ratingLabel(e.rating)}</span></p> : null}
        <EntryExtras e={e} />
        <PostImages images={e.images} who={e.username} what="diary entry" />
        <ReactionBar entry={e} onChange={onChange} />
        <Comments kind="entry" id={e.id} count={e.comment_count || 0} onCount={(c) => c !== (e.comment_count || 0) && onChange({ ...e, comment_count: c })} />
      </div>
    </article>
  );
}

/** Double-tap (or double-click) anywhere on a card that isn't a button or link to like it, like Instagram. It only ever likes, never un-likes.
 *  The friend is told through their Activity bell (the server does that when a heart is added). */
export function DoubleTapLike({ liked, enabled, onLike, children }) {
  const last = useRef(0);
  const [burst, setBurst] = useState(false);
  useEffect(() => { if (!burst) return undefined; const t = setTimeout(() => setBurst(false), 800); return () => clearTimeout(t); }, [burst]);
  const onClick = (e) => {
    if (!enabled) return;
    if (e.target.closest && e.target.closest("button, a, input, textarea, select, label, summary")) { last.current = 0; return; }
    const now = Date.now();
    if (now - last.current > 350) { last.current = now; return; }
    last.current = 0;
    try { window.getSelection && window.getSelection().removeAllRanges(); } catch { /* ignore */ }
    setBurst(true);
    if (!liked) onLike();
  };
  return (
    <div className="relative touch-manipulation" onClick={onClick} data-testid="like-area">
      {children}
      {burst && (
        <span className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center" aria-hidden="true" data-testid="like-burst">
          <Heart className="h-24 w-24 animate-ping fill-[#C89B3C] text-[#C89B3C] opacity-80" />
        </span>
      )}
    </div>
  );
}

/** What your friends have shared, newest first, with a box to post something yourself. */
export default function FeedTab({ ShowPicker, onGoFriends }) {
  const dialog = useDialog();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [filter, setFilter] = useState("all");
  const [composing, setComposing] = useState(false);

  const load = useCallback(() => {
    // Posts, lists and notes come from /feed; friends' diary entries from /friends/feed. They are merged, newest first.
    // If the diary request fails the rest still shows.
    Promise.all([api.get("/feed"), api.get("/friends/feed").catch(() => ({ data: [] }))])
      .then(([a, b]) => {
        const entries = (Array.isArray(b.data) ? b.data : []).map((e) => ({ ...e, kind: "entry" }));
        const all = [...a.data, ...entries].sort((x, y) => String(y.created_at || "").localeCompare(String(x.created_at || ""))).slice(0, 60);
        setItems((prev) => keepPhotoLinks(prev, all));
      })
      .catch(() => setItems((x) => x || []));
  }, []);
  useEffect(() => {
    load();
    const onFocus = () => load();
    const onVis = () => { if (document.visibilityState === "visible") load(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVis);
    const t = setInterval(load, 60000);
    return () => { window.removeEventListener("focus", onFocus); document.removeEventListener("visibilitychange", onVis); clearInterval(t); };
  }, [load]);

  const patch = (u) => setItems((a) => a.map((x) => (x.id === u.id && x.kind === (u.kind || x.kind) ? { ...x, ...u } : x)));
  const like = async (it) => {
    const url = it.kind === "entry" ? `/diary/${it.id}/reaction` : `/content/${it.kind}/${it.id}/reaction`;
    try {
      await api.put(url, { type: "heart", on: true });
      patch({ ...it, mine: [...(it.mine || []), "heart"], reactions: { ...(it.reactions || {}), heart: ((it.reactions || {}).heart || 0) + 1 } });
    } catch { toast.error("Could not save that like. Please try again."); }
  };
  const remove = async (p) => {
    if (!(await dialog.confirm({ title: "Delete this post?", body: "It will be deleted for good, along with any comments on it.", confirmLabel: "Delete", danger: true }))) return;
    try { await api.delete(`/content/post/${p.id}`); toast.success("Post deleted"); load(); }
    catch { toast.error("Could not delete that. Please try again."); }
  };

  const shown = items ? (filter === "all" ? items : items.filter((it) => it.kind === filter)) : null;

  return (
    <div className="mx-auto max-w-3xl space-y-6 md:mx-0" data-testid="feed-tab">
      {dialog.node}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="font-serif text-2xl font-semibold">Feed</h2>
          <p className="text-sm text-muted-foreground">What your friends are up to. Double-tap anything to like it.</p>
        </div>
        <button type="button" onClick={() => setComposing(true)} data-testid="add-post" className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full bg-[#9E1B28] px-5 py-2.5 text-sm font-semibold text-amber-50 hover:bg-[#B3202F]"><Plus className="h-4 w-4" /> Add post</button>
      </div>
      {composing && (
        <Modal title="New post" onClose={() => setComposing(false)} testid="compose-modal" wide backdropCloses={false}>
          <div className="mt-3">
            <PostComposer ShowPicker={ShowPicker} defaultVisibility={FEED_POST_DEFAULT} onPosted={() => { setComposing(false); load(); }} onCancel={() => setComposing(false)} />
          </div>
        </Modal>
      )}
      {items && items.length > 0 && (
        <div className="flex flex-wrap gap-2" role="group" aria-label="Filter the feed" data-testid="feed-filters">
          {FILTERS.map(([k, label]) => (
            <button key={k} type="button" onClick={() => setFilter(k)} aria-pressed={filter === k} data-testid={`feed-filter-${k}`}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${filter === k ? "bg-[#C89B3C] text-black" : "border border-border text-muted-foreground hover:text-foreground"}`}>{label}</button>
          ))}
        </div>
      )}
      {!items ? <p className="text-muted-foreground">Loading…</p> : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-8 text-center" data-testid="feed-empty">
          <Users className="mx-auto h-8 w-8 text-[#C89B3C]" />
          <p className="mt-3 font-serif text-xl font-semibold">Nothing here yet</p>
          <p className="mx-auto mt-1 max-w-md text-sm text-muted-foreground">Post above, or add friends. Their posts, lists and notes will show up here.</p>
          {onGoFriends && <button type="button" onClick={onGoFriends} data-testid="feed-find-friends" className="mt-4 rounded-full bg-[#C89B3C] px-6 py-2.5 text-sm font-semibold text-black">Find friends</button>}
        </div>
      ) : shown.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border bg-card p-6 text-center text-sm text-muted-foreground" data-testid="feed-filter-empty">Nothing of that kind yet.</p>
      ) : (
        <div className="space-y-5">
          {shown.map((it) => {
            const key = `${it.kind}-${it.id}`;
            const wrap = (card) => <DoubleTapLike key={key} enabled={!it.mine_post} liked={(it.mine || []).includes("heart")} onLike={() => like(it)}><FeedAuthor item={it} />{card}</DoubleTapLike>;
            if (it.kind === "post") {
              if (editing === it.id) return <PostComposer key={key} ShowPicker={ShowPicker} initial={it} onCancel={() => setEditing(null)} onPosted={() => { setEditing(null); load(); }} />;
              return wrap(<PostCard post={it} showAuthor={false} mine={!!it.mine_post} canReact={!it.mine_post} canReport={!it.mine_post} onEdit={it.mine_post ? (p) => setEditing(p.id) : undefined} onDelete={remove} onChange={patch} />);
            }
            if (it.kind === "entry") return wrap(<FeedEntryCard entry={it} onChange={patch} />);
            if (it.kind === "list") return wrap(<ListCard list={it} canReact canReport canComment onChange={patch} />);
            return wrap(<NoteCard note={it} canReact canReport canComment onChange={patch} />);
          })}
        </div>
      )}
    </div>
  );
}